import {
  SEARCHABLE_VENDOR_TAXONOMY_KEYS,
  SearchableVendorTaxonomyKey,
  VendorTaxonomyKey,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomyId } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { getVendorWordCategory } from '@zola-helpers/client/dist/es/marketplace/vendorWords';

import _compact from 'lodash/compact';
import { ParsedQuery } from 'query-string';

import { US_STATES } from '~/components/common/form/addresses';
import { findSlugsInPath } from '~/hooks/usePathSlugs';
import { ClientAwardGroupsEnum } from '~/meta/awards';
import {
  VendorSearchSorting,
  VendorSearchRequest,
  PartialVendorSearch,
} from '~/pages/couples/explore/components/SearchResults/types/types';
import {
  TaxonomyNodeView,
  LocationOrAllMarketsType,
  TaxonomyNodeType,
} from '~/types/responseTypes';
import {
  VendorSearchRangeType,
  MetroTypes,
  CatererTypes,
  OptionFacetType,
  OptionFacetCategoryType,
} from '~/types/types';
import { formatDate, isValidDate, parseDate } from '~/util/dateUtils';
import { FacetsByVendorType, SearchableFacet } from '~/util/facetUtils';
import Logger from '~/util/logger';

import { getFacetFromPriceRange } from '../components/SearchResults/components/SearchControls/facets/PriceFacetForm/getFacetFromPriceRange';
import {
  isSortDirection,
  isSortKey,
  sortingByVendorType,
} from '../components/SearchResults/utils/sortHelpers';
import {
  CAPACITY_FILTER,
  createAwardGroupsFilter,
  createLocationFilter,
  createPricingFilter,
} from '../meta/facets';
import { CanonicalUrlQueryParams, createCanonicalUrl, createSeoCanonicalUrl } from './canonicalUrl';
import { extractMinMaxFromSlug, MAX_RANGE_INDICATOR } from './helpers';

/**
 * Helper, that, when part of the parser is loaded, goes through the
 * slugs found in the path and sets the right fields in the component.
 *
 * @param {Object} lookup - a lookup of slug => object (facet, market, taxonomy)
 * @param {Array<string>} pathSlugs - the slugs parsed from the path
 */

function lookupHelper<K extends TaxonomyNodeView | SearchableFacet>(
  lookup: Record<string, K> | undefined,
  pathSlugs: string[]
) {
  if (!lookup) {
    // This shouldn't happen, but because we do lookups into objects, its a possibility
    Logger.error(new Error('LookupType is undefined in parseVendorSearchUrl'));
    return [];
  }

  const findOrFilterResults = pathSlugs.filter((slug) => lookup[slug]);
  const mapped = findOrFilterResults.map((slug) => lookup[slug]);
  return _compact(mapped);
}

/**
 * Helper for setting numeric ranges (price, capacity)
 *
 * @param {string} min - min value found in the query params (capacity-min=10)
 * @param {string} max  - max-value found in the query params (price-max=1000)
 * @param {function} setter - callback to set the numeric range
 */
const rangeHelper = (min: string | undefined | null, max: string | undefined | null) => {
  let parsedMin = NaN;
  if (min && min.match(/^\d+$/)) {
    parsedMin = parseInt(min, 10);
  }

  let parsedMax: number | typeof MAX_RANGE_INDICATOR = NaN;
  if (max) {
    if (max.match(/^\d+$/)) {
      parsedMax = parseInt(max, 10);
    }
    if (max === MAX_RANGE_INDICATOR) {
      parsedMax = MAX_RANGE_INDICATOR;
    }
  }

  if (parsedMin >= 0 && Number.isNaN(parsedMax)) {
    // If we have a min, but no max, that means no upper limit
    parsedMax = MAX_RANGE_INDICATOR;
  }

  let newRange: VendorSearchRangeType | null = {
    min: parsedMin,
    max: parsedMax,
  };

  // if we got both, min has to be less than max
  if (parsedMin && parsedMax && parsedMax !== MAX_RANGE_INDICATOR && parsedMin > parsedMax) {
    newRange = null;
  }

  return newRange;
};

function isValidSortingType(
  query: CanonicalUrlQueryParams,
  nextVendorSearch: VendorSearchRequest,
  selectedLocation?: LocationOrAllMarketsType | null
): query is VendorSearchSorting {
  const allowedSorts = sortingByVendorType(
    nextVendorSearch.selectedTaxonomy?.key,
    selectedLocation,
    nextVendorSearch.searchName
  );

  return (
    isSortKey(query.sortBy) &&
    isSortDirection(query.sortOrder) &&
    // Validate this sortBy and order are a valid combination
    allowedSorts.some((sortOption) => {
      if (sortOption.sortBy) {
        const { sortBy, sortOrder } = sortOption;
        return sortBy === query.sortBy && sortOrder === query.sortOrder;
      }
      return false;
    })
  );
}

const STATE_SLUGS = US_STATES.map((state) => state.slug);

/**
 * Look for slugs that represent the location & category in the path
 */
export const locationAndCategorySlugInPath = (
  pathSlugs: string[]
): {
  location: string | null;
  taxonomyKey: VendorTaxonomyKey | null;
} => {
  // Find the index of the category slug
  const vendorTypeIndex = pathSlugs.findIndex((slug) => {
    if (SEARCHABLE_VENDOR_TAXONOMY_KEYS.includes(slug as SearchableVendorTaxonomyKey)) {
      return true;
    }
    return false;
  });
  const taxonomyKey =
    vendorTypeIndex > -1 ? (pathSlugs[vendorTypeIndex] as VendorTaxonomyKey) : null;
  let location = null;
  // If there is a slug before the category, it should be the location
  if (vendorTypeIndex > 0) {
    location = pathSlugs[vendorTypeIndex - 1];
  }
  // If there is no category slug, try the first or second slug as the location
  else if (vendorTypeIndex === -1 && pathSlugs.length > 0) {
    if (STATE_SLUGS.includes(pathSlugs[0]) && pathSlugs[1]) {
      location = pathSlugs[1]; // Handles cases like california--los-angeles-ca
    } else {
      location = pathSlugs[0];
    }
  }
  return {
    location,
    taxonomyKey,
  };
};

const getSelectedTaxonomy = (taxonomyKey: string | null): TaxonomyNodeType | null => {
  if (taxonomyKey) {
    return {
      id: getVendorTaxonomyId(taxonomyKey),
      key: taxonomyKey,
      label: getVendorWordCategory(taxonomyKey),
    } as TaxonomyNodeType;
  }
  return null;
};

export const getSelectedFacetOptionsFromVendorSearch = (
  nextVendorSearch: VendorSearchRequest
): OptionFacetType[] => {
  const specialFacets: OptionFacetType[] = [];
  const specialCategories: {
    [key: string]: {
      vendorSearch: VendorSearchRangeType | null | undefined;
      filter: OptionFacetCategoryType;
    };
  } = {
    price: {
      vendorSearch: nextVendorSearch.price,
      filter: createPricingFilter(),
    },
    capacity: { vendorSearch: nextVendorSearch.capacity, filter: CAPACITY_FILTER },
  };
  Object.keys(specialCategories).forEach((categorySlug) => {
    const parsedRangeFromUrl = specialCategories[categorySlug].vendorSearch;

    const category = specialCategories[categorySlug].filter;

    if (
      parsedRangeFromUrl &&
      parsedRangeFromUrl.min !== null &&
      parsedRangeFromUrl.max &&
      category &&
      category.facets
    ) {
      // min could be 20,0000 and max 999999
      // for nyc venues, we should pick 20,0000 - 50,0000 and, 50000-999999
      category.facets.forEach((specialFacet: OptionFacetType) => {
        const { min: minFromFacet, max: maxFromFacet } = extractMinMaxFromSlug(specialFacet.slug);
        // If the facet (ie, one of the tags we can search by)
        // is in the range from the URL, turn it on.
        // minFromFacet: 0, 4000, 8000
        // maxFromFacet  4000, 8000, 'max'
        if (
          minFromFacet >= parsedRangeFromUrl.min &&
          (parsedRangeFromUrl.max === MAX_RANGE_INDICATOR ||
            (maxFromFacet as number) <= parsedRangeFromUrl.max)
        ) {
          specialFacets.push(specialFacet);
        }
      });
    }
  });

  let awardGroupsFacets: OptionFacetType[] = [];
  if (nextVendorSearch.awardGroups) {
    const awardGroupsCategory = nextVendorSearch.selectedTaxonomy?.key
      ? createAwardGroupsFilter()
      : undefined;
    const selectedAwardGroupsFacets = awardGroupsCategory?.facets?.filter((f) =>
      nextVendorSearch.awardGroups?.[0]?.includes(f.key as ClientAwardGroupsEnum)
    );
    awardGroupsFacets = selectedAwardGroupsFacets || [];
  }

  let metroTypeFacets: OptionFacetType[] = [];
  if (nextVendorSearch.metroTypes) {
    const metroTypeCategory =
      nextVendorSearch.selectedTaxonomy?.key && nextVendorSearch.selectedLocation?.type === 'CITY'
        ? createLocationFilter(nextVendorSearch.selectedTaxonomy.key as SearchableVendorTaxonomyKey)
        : undefined;
    const selectedMetroTypeFacets = metroTypeCategory?.facets?.filter((f) =>
      nextVendorSearch.metroTypes?.includes(f.key as MetroTypes)
    );
    metroTypeFacets = selectedMetroTypeFacets || [];
  }

  const fakeFacetGroup = [...awardGroupsFacets, ...metroTypeFacets];

  const priceFacet = getFacetFromPriceRange(
    nextVendorSearch.price?.min,
    nextVendorSearch.price?.max
  );

  if (priceFacet) {
    fakeFacetGroup.push(priceFacet);
  }
  const selectedFacetOptions = (nextVendorSearch.selectedFacets || []).concat([
    ...specialFacets,
    ...fakeFacetGroup,
  ]);
  return selectedFacetOptions;
};

export type ParseVendorSearchUrlResult = {
  vendorSearch: VendorSearchRequest;
  canonicalUrl: string;
  seoCanonicalUrl: string;
  selectedFacetOptions: OptionFacetType[];
};

export const parseVendorSearchUrl = ({
  slug,
  query,
  facetsByVendorType,
  selectedLocation,
  searchName,
  seoOptimized = false,
  allowUnclaimedListings = true,
}: {
  slug: string;
  query: ParsedQuery<string>;
  facetsByVendorType: FacetsByVendorType;
  selectedLocation: LocationOrAllMarketsType | null;
  searchName: VendorSearchRequest['searchName'];
  seoOptimized?: VendorSearchRequest['seoOptimized'];
  allowUnclaimedListings: VendorSearchRequest['allowUnclaimedListings'];
}): ParseVendorSearchUrlResult => {
  const pathSlugs = findSlugsInPath(slug ?? '');
  const { taxonomyKey } = locationAndCategorySlugInPath(pathSlugs);
  const selectedTaxonomy = getSelectedTaxonomy(taxonomyKey);

  const nextVendorSearch: VendorSearchRequest = {
    selectedLocation,
    selectedTaxonomy,
    page: 'page' in query && !Number.isNaN(Number(query.page)) ? Number(query.page) : 1,
    searchName,
    seoOptimized,
    allowUnclaimedListings,
  };
  // --------------------------------------------------------------------------
  // Find facets that are valid for the vendor type
  // --------------------------------------------------------------------------
  nextVendorSearch.selectedFacets =
    (selectedTaxonomy && lookupHelper(facetsByVendorType[selectedTaxonomy.key], pathSlugs)) || null;

  // --------------------------------------------------------------------------
  // Search term, ?name=501%20Union
  // --------------------------------------------------------------------------
  nextVendorSearch.searchTerm = (query.name as string | null) || null;

  // --------------------------------------------------------------------------
  // availability-date=2021-01-20
  // --------------------------------------------------------------------------
  const dates = (query['availability-dates'] as string[] | null)?.map((date) =>
    parseDate(date, 'YYYY-MM-DD')
  );

  if (dates?.every((date) => isValidDate(date))) {
    nextVendorSearch.availabilityDates = dates.map((date) => formatDate(date, 'YYYY-MM-DD'));
  }

  // --------------------------------------------------------------------------
  // AwardGroups
  // --------------------------------------------------------------------------
  const awardGroupsInParams = query['award-groups'] as string | null;
  nextVendorSearch.awardGroups = [
    (awardGroupsInParams?.split(',') as ClientAwardGroupsEnum[]) || null,
  ];

  // --------------------------------------------------------------------------
  // price
  // --------------------------------------------------------------------------
  const priceMin = query['price-min'] as string | null;
  const priceMax = query['price-max'] as string | null;
  nextVendorSearch.price = rangeHelper(priceMin, priceMax);

  // --------------------------------------------------------------------------
  // Capacity
  // --------------------------------------------------------------------------
  const capacityMin = query['capacity-min'] as string | null;
  const capacityMax = query['capacity-max'] as string | null;
  nextVendorSearch.capacity = rangeHelper(capacityMin, capacityMax);

  // --------------------------------------------------------------------------
  // CatererType
  // --------------------------------------------------------------------------
  // Note: This filter was probably removed
  const catererTypeInParams = query['caterer-type'] as string | null;
  nextVendorSearch.catererTypes = (catererTypeInParams?.split(',') as CatererTypes[]) || null;

  // --------------------------------------------------------------------------
  // MetroType, these aren't array bracketed when there are multiple
  // --------------------------------------------------------------------------
  const metroTypesInParams = query['metro-types'] as string | null;
  nextVendorSearch.metroTypes = (metroTypesInParams?.split(',') as MetroTypes[]) || null;

  // --------------------------------------------------------------------------
  // Sorting order query params
  // --------------------------------------------------------------------------
  nextVendorSearch.sorting = isValidSortingType(query, nextVendorSearch, selectedLocation)
    ? { sortBy: query.sortBy, sortOrder: query.sortOrder }
    : null;

  const selectedFacetOptions = getSelectedFacetOptionsFromVendorSearch(nextVendorSearch);

  // --------------------------------------------------------------------------
  // Use the search object to build a definitive URL
  // --------------------------------------------------------------------------
  const vendorSearch = nextVendorSearch as PartialVendorSearch;
  const canonicalUrl = createCanonicalUrl(vendorSearch, facetsByVendorType);
  const seoCanonicalUrl = createSeoCanonicalUrl(vendorSearch, facetsByVendorType);

  return {
    selectedFacetOptions,
    seoCanonicalUrl,
    canonicalUrl,
    vendorSearch: nextVendorSearch,
  };
};
